import { BufferAttribute, BufferGeometry, Mesh, type Material } from 'three'
import type { MachineCollider, MachineDefinition } from '@core/machine'
import { resolveSettings, settingText } from '@core/machine/settings'
import type { BuiltMeshes, Finish, PartPalette } from './partsKit'
import type asset from '../../SourceArt/PierPosts/mesh.json'
let data: typeof asset | undefined
export async function prepareAuthoredPosts(): Promise<void> {
  data ??= (await import('../../SourceArt/PierPosts/mesh.json')).default
}
export function postCentre(c: Extract<MachineCollider, { kind: 'circle' }>, d: MachineDefinition): readonly [number,number] {
  const a=c.adjustedBy
  const value=a?.offsets[settingText(resolveSettings(d),a.setting,'')] ?? 0
  const by=Number.isFinite(value)?value:0
  return a?.axis==='y'?[c.centre[0],c.centre[1]+by]:[c.centre[0]+by,c.centre[1]]
}
function instanceFor(d: MachineDefinition,c: MachineCollider) {
  const item=data?.machines[d.machineId as keyof typeof asset.machines]?.find(p=>p.id===c.id)
  if(item===undefined||c.kind!=='circle'||c.device!==undefined||c.material!==item.collider.material
    ||c.layer!==item.collider.layer||Math.abs(c.radius-item.collider.radius)>1e-9||!c.centre.every(Number.isFinite))return undefined
  return item
}
export function postPlacementKey(d: MachineDefinition): string {
  return d.colliders.filter(c=>instanceFor(d,c)!==undefined).map(c=>`${c.id}:${postCentre(c as Extract<MachineCollider,{kind:'circle'}>,d).join(',')}`).join(';')
}
/** Local geometry follows the caller's current collider plus the same resolved adjustment as the solver. */
export function authoredPost(d: MachineDefinition,c: MachineCollider,palette: PartPalette,_illustrated: boolean,steel?: Material): BuiltMeshes | undefined {
  const item=instanceFor(d,c)
  if(item===undefined)return undefined
  const geometries: BufferGeometry[]=[];const meshes: Mesh[]=[]
  try {
    const finishes=new Set<string>()
    const parts=data?.templates[item.template as keyof typeof asset.templates]?.parts
    if(parts===undefined||parts.length!==3)throw new Error('Post requires three groups')
    for(const p of parts) {
      if(!['HardwareRubber','HardwareIvory','HardwareSteel'].includes(p.finish)||finishes.has(p.finish))throw new Error('Invalid post finish')
      finishes.add(p.finish)
      const count=p.positions.length/3
      if(!Number.isInteger(count)||count===0||p.normals.length!==p.positions.length||p.uvs.length!==count*2||p.triangles.length===0||p.triangles.length%3!==0
        ||![...p.positions,...p.normals,...p.uvs].every(Number.isFinite)||!p.triangles.every(i=>Number.isInteger(i)&&i>=0&&i<count))throw new Error('Invalid post arrays')
      const g=new BufferGeometry();geometries.push(g)
      g.setAttribute('position',new BufferAttribute(new Float32Array(p.positions),3));g.setAttribute('normal',new BufferAttribute(new Float32Array(p.normals),3))
      g.setAttribute('uv',new BufferAttribute(new Float32Array(p.uvs),2));g.setIndex(p.triangles);g.computeBoundingBox();g.computeBoundingSphere()
      const finish=(p.finish[0]!.toLowerCase()+p.finish.slice(1)) as Finish
      const material=p.finish==='HardwareIvory'?palette.satinPostPolymer():p.finish==='HardwareSteel'&&steel!==undefined?steel:palette.get(finish)
      const mesh=new Mesh(g,material);mesh.name=`${c.id}.post.authored.${p.finish}`;meshes.push(mesh)
    }
    return {meshes,geometries}
  } catch(error) { geometries.forEach(g=>g.dispose());throw error }
}
